// The one workspace frame (D04, D05, D15, D16) used by all seven persona workspaces:
// a section nav (vertical side nav >= 1024px, scrollable strip below), and a header with the
// workspace as an eyebrow, the current section as the page's only <h1>, an optional context chip,
// a date-range slot, an actions slot and one keyboard-shortcut affordance.
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Keyboard, PanelLeftClose, PanelLeftOpen, type LucideIcon } from 'lucide-react';
import { Breadcrumbs, type Crumb } from './Breadcrumbs';
import { Dialog } from './Dialog';
import { cx } from './cx';
import { useHotkeys } from './hotkeys';
import { usePageMeta } from '../lib/meta';

export interface Section {
  slug: string;
  label: string;
  count?: number;
}

export interface Shortcut {
  keys: string;
  does: string;
}

const COLLAPSE_KEY = 'fl.ws-nav-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false; // Storage blocked: the nav simply starts expanded (a per-viewer convenience only).
  }
}

/** Index of the section the pathname is in: the longest matching slug wins; '' is the index. */
export function activeSection(pathname: string, base: string, sections: readonly Section[]): number {
  let best = -1;
  let bestLen = -1;
  sections.forEach((s, i) => {
    const href = s.slug ? `${base}/${s.slug}` : base;
    const hit = s.slug
      ? pathname === href || pathname.startsWith(`${href}/`)
      : pathname === base || pathname === `${base}/`;
    if (hit && href.length > bestLen) {
      best = i;
      bestLen = href.length;
    }
  });
  return best;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="fl-kbd">{children}</kbd>;
}

export function WorkspaceFrame({
  workspace,
  job,
  base,
  sections,
  icon: WsIcon,
  title,
  description,
  context,
  dateRange,
  actions,
  shortcuts = [],
  breadcrumbs,
  children,
}: {
  /** Workspace name: the same string in the switcher, eyebrow, <title> and palette (D06). */
  workspace: string;
  job: string;
  base: string;
  sections: readonly Section[];
  icon?: LucideIcon;
  /** Overrides the h1 (detail pages); defaults to the current section label. */
  title?: string;
  description?: ReactNode;
  /** e.g. the organisation this workspace acts for. */
  context?: ReactNode;
  dateRange?: ReactNode;
  actions?: ReactNode;
  shortcuts?: Shortcut[];
  /** Trail above the h1 for a page nested under a section (D12), e.g. Applications / Senior Engineer. */
  breadcrumbs?: readonly Crumb[];
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [help, setHelp] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const found = activeSection(pathname, base, sections);
  const active = found < 0 ? 0 : found;
  const section = sections[active];
  const h1 = title ?? section?.label ?? workspace;
  usePageMeta(h1 === workspace ? workspace : `${h1} · ${workspace}`);

  const hrefOf = (s: Section) => (s.slug ? `${base}/${s.slug}` : base);
  const go = (d: number) =>
    navigate(hrefOf(sections[(active + d + sections.length) % sections.length]!));
  useHotkeys({ '?': () => setHelp(true), '[': () => go(-1), ']': () => go(1) });

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
    } catch {
      // Storage blocked: the choice lasts for this page view only.
    }
  };

  const all: Shortcut[] = [
    { keys: '[  ]', does: 'Previous / next section' },
    { keys: '?', does: 'Show this list' },
    { keys: 'Ctrl K', does: 'Jump to any page or command' },
    ...shortcuts,
  ];

  return (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
      <aside
        className={cx(
          'min-w-0 lg:sticky lg:top-[calc(var(--topbar-h)+1.5rem)] lg:flex-none',
          collapsed ? 'lg:w-11' : 'lg:w-56',
        )}
      >
        <div className={cx('mb-3 hidden items-start gap-2 lg:flex', collapsed && 'lg:hidden')}>
          {WsIcon ? (
            <span className="mt-0.5 flex size-8 flex-none items-center justify-center rounded-sm bg-ink text-on-ink">
              <WsIcon size={17} aria-hidden="true" />
            </span>
          ) : null}
          <div className="min-w-0">
            <p className="text-sm font-semibold">{workspace}</p>
            <p className="text-xs leading-snug text-ink-2">{job}</p>
          </div>
        </div>
        <nav
          aria-label={`${workspace} sections`}
          className={cx(
            'fl-fade-x scroll-x -mx-4 flex gap-1 border-hair-b px-4 lg:mx-0 lg:flex-col lg:border-0 lg:px-0 lg:[mask-image:none]',
            collapsed && 'lg:hidden',
          )}
        >
          {sections.map((s, i) => (
            <Link
              key={s.slug || 'index'}
              to={hrefOf(s)}
              aria-current={i === active ? 'page' : undefined}
              aria-label={s.count ? `${s.label} ${s.count} waiting` : undefined}
              className={cx(
                'fl-tab lg:justify-between lg:rounded-sm lg:border-b-0 lg:px-3 lg:text-[0.9375rem]',
                'lg:aria-[current=page]:bg-sheet lg:aria-[current=page]:shadow-1',
                'lg:hover:bg-paper-2',
              )}
            >
              <span>{s.label}</span>
              {s.count ? (
                <>
                  {' '}
                  <span className="fl-count" data-tone="gate" aria-hidden="true">
                    {s.count}
                  </span>
                </>
              ) : null}
            </Link>
          ))}
        </nav>
        <button
          type="button"
          onClick={toggle}
          className="fl-btn mt-3 hidden lg:inline-flex"
          data-variant="ghost"
          data-size="sm"
          aria-expanded={!collapsed}
          aria-label={collapsed ? `Show ${workspace} sections` : `Hide ${workspace} sections`}
        >
          {collapsed ? <PanelLeftOpen size={16} aria-hidden="true" /> : <PanelLeftClose size={16} aria-hidden="true" />}
          {collapsed ? null : <span className="text-ink-2">Hide</span>}
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
          <div className="flex min-w-0 flex-col gap-1.5">
            {breadcrumbs ? <Breadcrumbs items={breadcrumbs} /> : null}
            <p data-eyebrow className="fl-eyebrow flex flex-wrap items-center gap-2">
              {workspace}
              {context ? <span className="fl-chip text-xs">{context}</span> : null}
            </p>
            <h1>{h1}</h1>
            {description ? <div className="max-w-prose text-ink-2">{description}</div> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {dateRange}
            {actions}
            <button
              type="button"
              className="fl-btn"
              data-variant="ghost"
              data-size="sm"
              data-icon-only="true"
              onClick={() => setHelp(true)}
              aria-keyshortcuts="?"
              aria-label="Keyboard shortcuts"
              title="Keyboard shortcuts (?)"
            >
              <Keyboard size={18} aria-hidden="true" />
            </button>
          </div>
        </header>
        {children}
      </div>

      <Dialog open={help} onClose={() => setHelp(false)} title="Keyboard shortcuts">
        <table className="fl-table">
          <caption className="sr-only">Keyboard shortcuts</caption>
          <thead>
            <tr>
              <th scope="col">Keys</th>
              <th scope="col">Does</th>
            </tr>
          </thead>
          <tbody>
            {all.map((s) => (
              <tr key={s.keys}>
                <td>
                  <Kbd>{s.keys}</Kbd>
                </td>
                <td>{s.does}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Dialog>
    </div>
  );
}
